/**
 * All monetary amounts cross process boundaries as integer strings in the token's
 * smallest unit (USDC micro-units). Never floats: $10.10 is not representable in
 * binary floating point and escrow accounting must reconcile to the cent.
 */
export type Micros = string;

const MICROS_PER_UNIT = 1_000_000n;

export function usdToMicros(usd: string | number): Micros {
  const s = typeof usd === 'number' ? usd.toFixed(6) : usd.trim();
  if (!/^\d+(\.\d{1,6})?$/.test(s)) throw new Error(`invalid USD amount: ${usd}`);
  const [whole = '0', frac = ''] = s.split('.');
  return (BigInt(whole) * MICROS_PER_UNIT + BigInt(frac.padEnd(6, '0'))).toString();
}

/**
 * Non-throwing variant for UI paths that format as the user types. A partially
 * entered amount ("1.", "0.1234567") is not an error worth crashing a render
 * over — it is simply not a number yet.
 */
export function tryUsdToMicros(usd: string | number): Micros | null {
  try {
    return usdToMicros(usd);
  } catch {
    return null;
  }
}

export function microsToUsd(micros: Micros): string {
  const v = BigInt(micros);
  const whole = v / MICROS_PER_UNIT;
  const frac = (v % MICROS_PER_UNIT).toString().padStart(6, '0').replace(/0+$/, '');
  return frac ? `${whole}.${frac}` : `${whole}`;
}

export function formatUsd(micros: Micros): string {
  const v = BigInt(micros);
  const whole = v / MICROS_PER_UNIT;
  const cents = ((v % MICROS_PER_UNIT) / 10_000n).toString().padStart(2, '0');
  return `$${whole.toLocaleString('en-US')}.${cents}`;
}

/**
 * Platform cut, in basis points, taken only when an escrow is RELEASED to the
 * recipient. A refunded escrow returns the full amount to the sender: we earn
 * nothing when the recipient ignores the message, which is what makes the
 * refund promise credible.
 */
export const PLATFORM_FEE_BPS = 500; // 5%

export function splitRelease(amount: Micros, bps: number = PLATFORM_FEE_BPS): {
  toRecipient: Micros;
  toPlatform: Micros;
} {
  const total = BigInt(amount);
  const platform = (total * BigInt(bps)) / 10_000n;
  return { toRecipient: (total - platform).toString(), toPlatform: platform.toString() };
}
